import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Edit2, Briefcase, MapPin, User, Phone, Zap,
  ChevronLeft, ChevronRight, X, Save, Calendar, DollarSign
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STATUS_CONFIG = {
  lead: { label: 'Lead', color: 'bg-zinc-500/20 text-zinc-400' },
  cotizado: { label: 'Cotizado', color: 'bg-blue-500/20 text-blue-400' },
  aprobado: { label: 'Aprobado', color: 'bg-emerald-500/20 text-emerald-400' },
  en_planificacion: { label: 'En Planificación', color: 'bg-yellow-500/20 text-yellow-400' },
  compra_materiales: { label: 'Compra Materiales', color: 'bg-orange-500/20 text-orange-400' },
  en_instalacion: { label: 'En Instalación', color: 'bg-purple-500/20 text-purple-400' },
  en_revision: { label: 'En Revisión', color: 'bg-pink-500/20 text-pink-400' },
  completado: { label: 'Completado', color: 'bg-cyan-500/20 text-cyan-400' },
  entregado: { label: 'Entregado', color: 'bg-teal-500/20 text-teal-400' },
  en_garantia: { label: 'En Garantía', color: 'bg-indigo-500/20 text-indigo-400' },
  cancelado: { label: 'Cancelado', color: 'bg-red-500/20 text-red-400' }
};

const PROJECT_TYPES = [
  { value: 'residencial', label: 'Residencial' },
  { value: 'comercial', label: 'Comercial' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'agro', label: 'Agro' },
  { value: 'gobierno', label: 'Gobierno' }
];

const ProjectModal = ({ project, onClose, onSave }) => {
  const [formData, setFormData] = useState(project || {
    name: '', project_type: 'residencial',
    client_name: '', client_phone: '', client_email: '',
    address: '', city: 'Asunción', department: 'Central',
    system_capacity_kw: 0, panel_count: 0,
    quote_amount_gs: 0, status: 'lead', priority: 'media',
    progress_percent: 0
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">
            {project ? 'Editar Proyecto' : 'Nuevo Proyecto'}
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Info proyecto */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm text-zinc-400 mb-1">Nombre del Proyecto *</label>
              <input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Ej: Instalación Solar Familia García"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Tipo</label>
              <select
                value={formData.project_type}
                onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              >
                {PROJECT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Estado</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              >
                {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Cliente */}
          <div className="p-4 bg-zinc-800/50 rounded-lg space-y-4">
            <h4 className="text-sm font-medium text-emerald-400">Datos del Cliente</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Nombre *</label>
                <input
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  required
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Teléfono *</label>
                <input
                  value={formData.client_phone}
                  onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                  required
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm text-zinc-400 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.client_email || ''}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>
          </div>

          {/* Ubicación */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-3">
              <label className="block text-sm text-zinc-400 mb-1">Dirección *</label>
              <input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Ciudad</label>
              <input
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Departamento</label>
              <input
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>

          {/* Sistema */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Capacidad (kW) *</label>
              <input
                type="number"
                value={formData.system_capacity_kw}
                onChange={(e) => setFormData({ ...formData, system_capacity_kw: parseFloat(e.target.value) || 0 })}
                required
                min="0"
                step="0.1"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Paneles *</label>
              <input
                type="number"
                value={formData.panel_count}
                onChange={(e) => setFormData({ ...formData, panel_count: parseInt(e.target.value) || 0 })}
                required
                min="0"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Monto (Gs) *</label>
              <input
                type="number"
                value={formData.quote_amount_gs}
                onChange={(e) => setFormData({ ...formData, quote_amount_gs: parseFloat(e.target.value) || 0 })}
                required
                min="0"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>

          {/* Progreso */}
          <div>
            <label className="block text-sm text-zinc-400 mb-1">Progreso: {formData.progress_percent}%</label>
            <input
              type="range"
              value={formData.progress_percent}
              onChange={(e) => setFormData({ ...formData, progress_percent: parseInt(e.target.value) })}
              min="0"
              max="100"
              className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800">
            <button type="button" onClick={onClose} className="px-4 py-2 text-zinc-400 hover:text-white">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ProjectsPage = () => {
  const { fetchWithAuth, hasPermission } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [page, setPage] = useState(0);
  const limit = 20;

  const canCreate = hasPermission('projects_create');
  const canEdit = hasPermission('projects_edit');

  const loadProjects = async () => {
    setLoading(true);
    try {
      let url = `/api/projects?skip=${page * limit}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const res = await fetchWithAuth(url);
      if (res.ok) {
        const data = await res.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error cargando proyectos:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadProjects();
  }, [page, statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    loadProjects();
  };

  const handleSaveProject = async (projectData) => {
    try {
      const url = editingProject 
        ? `/api/projects/${editingProject.id}`
        : '/api/projects';
      
      const res = await fetchWithAuth(url, {
        method: editingProject ? 'PUT' : 'POST',
        body: JSON.stringify(projectData)
      });

      if (res.ok) {
        setShowModal(false);
        setEditingProject(null);
        loadProjects();
      } else {
        const error = await res.json();
        alert(error.detail || 'Error al guardar');
      }
    } catch (error) {
      alert('Error de conexión');
    }
  };

  const formatCurrency = (value) => new Intl.NumberFormat('es-PY').format(value);

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Proyectos</h1>
          <p className="text-zinc-400 text-sm">Gestión de instalaciones solares</p>
        </div>
        {canCreate && (
          <button
            onClick={() => { setEditingProject(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
            data-testid="add-project-btn"
          >
            <Plus className="w-4 h-4" />
            Nuevo Proyecto
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar proyecto o cliente..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-zinc-500 focus:outline-none focus:border-emerald-500"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg">
            Buscar
          </button>
        </form>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_CONFIG).map(([value, config]) => (
            <option key={value} value={value}>{config.label}</option>
          ))}
        </select>
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No hay proyectos</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => {
              const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG.lead;
              
              return (
                <div
                  key={project.id}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium text-white line-clamp-1">{project.name}</p>
                      <p className="text-xs text-zinc-500">{project.project_code}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${statusConfig.color}`}>
                      {statusConfig.label}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    <p className="flex items-center gap-2 text-zinc-400">
                      <User className="w-4 h-4 text-zinc-500" />
                      {project.client_name}
                    </p>
                    <p className="flex items-center gap-2 text-zinc-400">
                      <MapPin className="w-4 h-4 text-zinc-500" />
                      {project.city}, {project.department}
                    </p>
                    <p className="flex items-center gap-2 text-zinc-400">
                      <Zap className="w-4 h-4 text-zinc-500" />
                      {project.system_capacity_kw} kW • {project.panel_count} paneles
                    </p>
                    <p className="flex items-center gap-2 text-white font-medium">
                      <DollarSign className="w-4 h-4 text-emerald-400" />
                      {formatCurrency(project.quote_amount_gs)} Gs
                    </p>
                  </div>

                  {/* Progress */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-zinc-500">Progreso</span>
                      <span className="text-white">{project.progress_percent}%</span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 transition-all"
                        style={{ width: `${project.progress_percent}%` }}
                      ></div>
                    </div>
                  </div>

                  {canEdit && (
                    <button
                      onClick={() => { setEditingProject(project); setShowModal(true); }}
                      className="flex items-center justify-center gap-2 w-full py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                      Editar
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-zinc-500">Página {page + 1}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="p-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 rounded-lg"
              >
                <ChevronLeft className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={projects.length < limit}
                className="p-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 rounded-lg"
              >
                <ChevronRight className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Modal */}
      {showModal && (
        <ProjectModal
          project={editingProject}
          onClose={() => { setShowModal(false); setEditingProject(null); }}
          onSave={handleSaveProject}
        />
      )}
    </div>
  );
};

export default ProjectsPage;
